import React, { useState, useEffect } from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from '@mui/material';
import HeadsetIcon from '@mui/icons-material/Headset';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../../core/axiosInstance';
import liff from '@line/liff';
import PaymentFacts from '../common/PaymentFacts';
import { amountDue } from '../common/amountDue';
const PaymentDetail = () => {
  const [selectedOption, setSelectedOption] = useState('qr');
  const [qrBase64, setQrBase64] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate(); // For navigation
  const { id } = useParams(); // Get ID from URL
  // Fetch contract details and QR code when component mounts or contractId changes
  useEffect(() => {
    liff
      .init({
        liffId: import.meta.env.VITE_LIFF_ID,
      })
      .then(async () => {
        console.log('LIFF init succeeded');
      })
      .catch((e) => {
        alert(`พบข้อผิดพลาด: ${e.message}`);
      });

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [contractResponse, listResponse] = await Promise.all([
          api.get(`/api/contracts/me?contractId=${id}`),
          api.get('/api/contracts/me'),
        ]);
        const detail = contractResponse.data[0];
        const due = amountDue(detail, listResponse.data);
        if (due == null) {
          throw new Error(
            `contracts/me has no minimumPayment for contract ${id}`
          );
        }
        detail.minimumPayment = due;
        setContractData(detail);

        const qrResponse = await api.get(
          `/api/company/${detail.companyId}`
        );
        const qrData = qrResponse.data;
        setCompanyData(qrData);
        setQrBase64(qrData.qRcode); // Assuming API returns { base64: "..." }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  const handleSaveQr = () => {
    if (qrBase64) {
      liff.openWindow({
        url: `/qr/${contractData.companyId}`,
      });
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Top Navbar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={handleBackClick}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 'medium' }}
          >
            {contractData?.contractNumber}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content Container */}
      <Box
        sx={{
          pt: 8, // Space for fixed AppBar
          pb: 2,
          display: 'flex',
          justifyContent: 'center',
          // alignItems: 'center',
          // minHeight: '100vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 480,
            borderRadius: 2,
            overflow: 'hidden',
            m: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {!loading && !error && contractData && (
              <Box sx={{ mb: 2 }}>
                <PaymentFacts contract={contractData} />
                <Divider sx={{ mt: 2 }} />
              </Box>
            )}
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedOption}
                onChange={handleOptionChange}
                sx={{
                  gap: 1,
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  },
                }}
              >
                <FormControlLabel
                  value="qr"
                  control={<Radio color="primary" />}
                  label="ชำระดอกเบี้ย"
                  sx={{
                    bgcolor:
                      selectedOption === 'qr' ? '#e3f2fd' : 'transparent',
                    borderRadius: 1,
                    p: 1,
                    m: 0,
                  }}
                />
                <FormControlLabel
                  value="contact"
                  control={<Radio color="primary" />}
                  label="ชำระตัดต้น"
                  sx={{
                    bgcolor:
                      selectedOption === 'contact' ? '#e3f2fd' : 'transparent',
                    borderRadius: 1,
                    p: 1,
                    m: 0,
                  }}
                />
              </RadioGroup>

              {/* Content Area */}
              <Box
                sx={{
                  mt: 3,
                  minHeight: 150,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {loading ? (
                  <CircularProgress size={36} />
                ) : error ? (
                  <Typography
                    color="error"
                    align="center"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                  >
                    {error}
                  </Typography>
                ) : (
                  <>
                    {selectedOption === 'qr' && (
                      <div>
                        {qrBase64 && (
                          <img
                            src={`${qrBase64}`}
                            alt="QR Code"
                            style={{
                              width: '100%',
                              maxWidth: 256,
                              height: 'auto',
                              borderRadius: 4,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          />
                        )}

                        <Typography
                          variant="h7"
                          align="left"
                          sx={{ display: 'block' }}
                        >
                          ธนาคาร: {companyData?.qrBankName}
                          <br /> ชื่อบัญชี: {companyData?.qrBankAccountName}
                          <br /> เลขบัญชี: {companyData?.qrBankAccountNumber}
                        </Typography>

                        <Divider sx={{ mt: 2, mb: 1 }} />

                        {qrBase64 && (
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{ display: 'block' }}
                          >
                            กรุณาบันทึก QR
                            นี้และชำระเงินผ่านทางแอปพลิเคชันธนาคาร
                          </Typography>
                        )}
                        {!qrBase64 && (
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{ display: 'block' }}
                          >
                            กรุณาโอนเงินตามข้อมูลธนาคารนี้และแจ้งชำระเงินด้วย
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{ display: 'block' }}
                        >
                          <div flexitem="true">
                            <Typography
                              variant="caption"
                              sx={{ marginLeft: 1 }}
                            >
                              ยอดเงินที่ต้องชำระ
                            </Typography>
                            <Typography
                              variant="h6"
                              color="secondary"
                              sx={{ display: 'inline-block', marginLeft: 1 }}
                            >
                              {contractData?.minimumPayment.toLocaleString()}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ marginLeft: 1 }}
                            >
                              บาท
                            </Typography>
                          </div>
                        </Typography>
                        {qrBase64 && (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Button
                              onClick={handleSaveQr}
                              variant="contained"
                              color="primary"
                              startIcon={<SaveIcon />}
                              sx={{ mt: 2, alignSelf: 'center' }}
                            >
                              บันทึก QR Code
                            </Button>
                          </Box>
                        )}
                      </div>
                    )}

                    {selectedOption === 'contact' && (
                      <Button
                        onClick={() => {
                          const context = liff.getContext();

                          if (
                            context &&
                            (context.type === 'utou' ||
                              context.type === 'room' ||
                              context.type === 'group')
                          ) {
                            liff
                              .sendMessages([
                                {
                                  type: 'text',
                                  text: 'ต้องการชำระตัดต้น',
                                },
                              ])
                              .then(() => {
                                window.location.href = 'https://lin.ee/VmQqk56';
                              })
                              .catch((err) => {
                                alert('Error sending message: ' + err);
                              });
                          }
                        }}
                        variant="contained"
                        color="primary"
                        fullWidth
                      >
                        <HeadsetIcon style={{ marginRight: 5 }}> </HeadsetIcon>
                        <Typography variant="caption" align="center">
                          กรุณาติดต่อเจ้าหน้าที่
                        </Typography>
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </FormControl>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default PaymentDetail;
